export const CHAT_SCOPE = {
  LOUNGE: 'lounge',
  ROOM_LOUNGE: 'room_lounge',
  ROOM_GAME: 'room_game',
  DM: 'dm',
  DM_INBOX: 'dm_inbox',
};

export const getUserId = (user) => `${user?.id || user?._id || ''}`;

export const buildHomeLoungeTarget = () => ({
  id: 'home-lounge',
  label: 'Home Lounge',
  type: CHAT_SCOPE.LOUNGE,
  scope: CHAT_SCOPE.LOUNGE,
  roomId: null,
  recipientId: null,
  recipientName: null,
});

const buildRoomTargetLabel = (roomName, roomCode, roomId) => {
  const cleanName = `${roomName || ''}`.trim();
  const cleanCode = `${roomCode || ''}`.trim();
  if (cleanName && cleanCode) return `Room Chat: ${cleanName} (${cleanCode})`;
  if (cleanName) return `Room Chat: ${cleanName}`;
  if (cleanCode) return `Room Chat: ${cleanCode}`;
  return `Room Chat: ${roomId || 'Room'}`;
};

export const buildRoomLoungeTarget = (roomOrConfig) => {
  const roomId = typeof roomOrConfig === 'object' ? `${roomOrConfig?.roomId || roomOrConfig?.id || ''}` : `${roomOrConfig || ''}`;
  const roomName = typeof roomOrConfig === 'object' ? roomOrConfig?.roomName : null;
  const roomCode = typeof roomOrConfig === 'object' ? roomOrConfig?.roomCode : null;
  return {
    id: `room-game:${roomId}`,
    label: buildRoomTargetLabel(roomName, roomCode, roomId),
    type: CHAT_SCOPE.ROOM_GAME,
    scope: CHAT_SCOPE.ROOM_GAME,
    roomId,
    roomName: roomName || null,
    roomCode: roomCode || null,
    recipientId: null,
    recipientName: null,
  };
};

export const buildRoomGameTarget = (roomOrConfig) => buildRoomLoungeTarget(roomOrConfig);

export const buildDmTarget = (participant) => {
  const recipientId = `${participant?.userId || participant?.id || participant?._id || ''}`;
  const recipientName = participant?.displayName || participant?.username || participant?.email || 'Player';
  return {
    id: `dm:${recipientId}`,
    label: `DM: ${recipientName}`,
    type: CHAT_SCOPE.DM,
    scope: CHAT_SCOPE.DM,
    roomId: null,
    recipientId,
    recipientName,
  };
};

export const buildDmInboxTarget = () => ({
  id: 'dm-inbox',
  label: 'DM Inbox',
  type: CHAT_SCOPE.DM_INBOX,
  scope: CHAT_SCOPE.DM,
  roomId: null,
  recipientId: null,
  recipientName: null,
});

export const buildOutgoingMessage = (target, currentUser, body) => {
  const cleanBody = `${body || ''}`.trim();
  const senderId = getUserId(currentUser);
  const senderName = currentUser?.displayName || currentUser?.username || currentUser?.email || 'Player';
  const base = {
    senderId,
    senderUserId: senderId,
    senderName,
    body: cleanBody,
    createdAt: new Date().toISOString(),
  };

  if (target?.type === CHAT_SCOPE.LOUNGE) {
    return {
      ...base,
      scope: CHAT_SCOPE.LOUNGE,
      roomId: null,
      recipientId: null,
      recipientUserId: null,
      recipientName: null,
    };
  }

  if (target?.type === CHAT_SCOPE.ROOM_LOUNGE) {
    return {
      ...base,
      scope: CHAT_SCOPE.ROOM_GAME,
      roomId: target.roomId,
      recipientId: null,
      recipientUserId: null,
      recipientName: null,
    };
  }

  if (target?.type === CHAT_SCOPE.ROOM_GAME) {
    return {
      ...base,
      scope: CHAT_SCOPE.ROOM_GAME,
      roomId: target.roomId,
      recipientId: null,
      recipientUserId: null,
      recipientName: null,
    };
  }

  if (target?.type === CHAT_SCOPE.DM) {
    return {
      ...base,
      scope: CHAT_SCOPE.DM,
      roomId: null,
      recipientId: target.recipientId,
      recipientUserId: target.recipientId,
      recipientName: target.recipientName || null,
    };
  }

  if (target?.type === CHAT_SCOPE.DM_INBOX) {
    throw new Error('Cannot send from DM Inbox target');
  }

  throw new Error('Unknown chat target');
};

const inferLegacyScope = (message, fallbackScope = CHAT_SCOPE.LOUNGE) => {
  if (message?.scope) return message.scope;
  if (message?.recipientUserId || message?.recipientId) return CHAT_SCOPE.DM;
  if (fallbackScope === CHAT_SCOPE.ROOM_GAME || fallbackScope === CHAT_SCOPE.ROOM_LOUNGE) {
    return CHAT_SCOPE.ROOM_GAME;
  }
  return CHAT_SCOPE.LOUNGE;
};

export const toNormalizedMessage = ({ message, fallbackScope = CHAT_SCOPE.LOUNGE, roomId = null }) => {
  if (!message) return null;
  const scope = inferLegacyScope(message, fallbackScope);
  return {
    id: `${message?.messageId || message?._id || message?.id || `${message?.userId || ''}:${message?.createdAt || ''}:${message?.text || ''}`}`,
    senderId: `${message?.senderId || message?.userId || ''}`,
    senderUserId: `${message?.senderUserId || message?.senderId || message?.userId || ''}`,
    senderName: message?.senderName || message?.displayName || 'Player',
    scope,
    roomId: `${message?.roomId || roomId || ''}` || null,
    recipientId: `${message?.recipientId || message?.recipientUserId || ''}` || null,
    recipientUserId: `${message?.recipientUserId || message?.recipientId || ''}` || null,
    recipientName: message?.recipientName || message?.recipientDisplayName || null,
    body: message?.body || message?.text || '',
    createdAt: message?.createdAt || new Date(0).toISOString(),
  };
};

export const messageMatchesTarget = (message, target, currentUserId) => {
  if (!message || !target) return false;

  if (target.type === CHAT_SCOPE.LOUNGE) {
    return message.scope === CHAT_SCOPE.LOUNGE;
  }

  if (target.type === CHAT_SCOPE.ROOM_LOUNGE) {
    return (
      (message.scope === CHAT_SCOPE.ROOM_LOUNGE || message.scope === CHAT_SCOPE.ROOM_GAME)
      && `${message.roomId || ''}` === `${target.roomId || ''}`
    );
  }

  if (target.type === CHAT_SCOPE.ROOM_GAME) {
    return (
      (message.scope === CHAT_SCOPE.ROOM_GAME || message.scope === CHAT_SCOPE.ROOM_LOUNGE)
      && `${message.roomId || ''}` === `${target.roomId || ''}`
    );
  }

  if (target.type === CHAT_SCOPE.DM) {
    return (
      message.scope === CHAT_SCOPE.DM &&
      (
        (
          `${message.senderId || ''}` === `${currentUserId || ''}` &&
          `${message.recipientId || ''}` === `${target.recipientId || ''}`
        ) ||
        (
          `${message.senderId || ''}` === `${target.recipientId || ''}` &&
          `${message.recipientId || ''}` === `${currentUserId || ''}`
        )
      )
    );
  }

  if (target.type === CHAT_SCOPE.DM_INBOX) {
    return (
      message.scope === CHAT_SCOPE.DM &&
      `${message.recipientId || ''}` === `${currentUserId || ''}`
    );
  }

  return false;
};

export const getPlaceholderForTarget = (target) => {
  if (!target) return 'Type a message...';
  if (target.type === CHAT_SCOPE.LOUNGE) return 'Message Home Lounge...';
  if (target.type === CHAT_SCOPE.ROOM_LOUNGE || target.type === CHAT_SCOPE.ROOM_GAME) return 'Message room chat...';
  if (target.type === CHAT_SCOPE.DM) return `Message ${target.recipientName || 'player'}...`;
  if (target.type === CHAT_SCOPE.DM_INBOX) return 'Open a DM thread to reply...';
  return 'Type a message...';
};

export const dedupeAndSortNormalizedMessages = (messages = []) => {
  const seen = new Set();
  const ordered = [];
  messages.forEach((msg, idx) => {
    const key = [
      `${msg?.id || ''}`,
      `${msg?.senderId || ''}`,
      `${msg?.recipientId || ''}`,
      `${msg?.scope || ''}`,
      `${msg?.roomId || ''}`,
      `${msg?.body || ''}`,
      `${msg?.createdAt || idx}`,
    ].join('|');
    if (seen.has(key)) return;
    seen.add(key);
    ordered.push(msg);
  });
  return ordered.sort((a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime());
};

export const computeUnreadByTarget = ({
  targets = [],
  messages = [],
  currentUserId = '',
  lastSeenAtByTargetId = {},
}) => {
  const unreadByTargetId = {};
  targets.forEach((target) => {
    const seenAtRaw = lastSeenAtByTargetId?.[target.id] || null;
    const seenAtMs = seenAtRaw ? new Date(seenAtRaw).getTime() : 0;
    unreadByTargetId[target.id] = messages.filter((msg) => {
      if (!messageMatchesTarget(msg, target, currentUserId)) return false;
      if (`${msg?.senderId || ''}` === `${currentUserId || ''}`) return false;
      const createdMs = new Date(msg?.createdAt || 0).getTime();
      return Number.isFinite(createdMs) && createdMs > seenAtMs;
    }).length;
  });

  const notices = targets
    .map((target) => ({
      targetId: target.id,
      label: target.label,
      count: unreadByTargetId[target.id] || 0,
    }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count);

  return {
    unreadByTargetId,
    notices,
    totalUnread: notices.reduce((sum, entry) => sum + entry.count, 0),
  };
};
